import {
	rulesFor,
	matchesRules,
	frequencyAllows,
	rememberSlide,
	presentation,
	armTrigger,
	targetingContext,
} from "./popup-behavior";
import { mountPopupElements } from "./popup-elements";
const STORAGE_KEY = "solufy_website_popup_last_shown_" + encodeURIComponent(location.host);

function lastShown() {
	let stored = 0;
	try {
		stored = Number(localStorage.getItem(STORAGE_KEY)) || 0;
	} catch {}
	const cookie = document.cookie.split("; ").find((item) => item.startsWith(STORAGE_KEY + "="));
	return Math.max(stored, Number(cookie?.split("=")[1]) || 0);
}

function rememberShown(timestamp) {
	try {
		localStorage.setItem(STORAGE_KEY, String(timestamp));
	} catch {}
	document.cookie = `${STORAGE_KEY}=${timestamp}; Max-Age=3600; Path=/; SameSite=Lax${
		location.protocol === "https:" ? "; Secure" : ""
	}`;
}

async function claimAndShow(show) {
	const key = STORAGE_KEY + "_claim";
	const token = String(Date.now()) + ":" + Math.random();
	try {
		localStorage.setItem(key, token);
	} catch {
		show();
		return;
	}
	// Coordinate tabs on HTTP sites where the Web Locks API is unavailable.
	await new Promise((resolve) => setTimeout(resolve, 50));
	if (localStorage.getItem(key) === token) {
		show();
		localStorage.removeItem(key);
	}
}

function mountBanners(popup) {
	const slides = [...popup.querySelectorAll("[data-popup-slide]")];
	const single = popup.dataset.displayMode === "One Banner Per Visit";
	const navigation = popup.querySelector("[data-popup-navigation]");
	const key = STORAGE_KEY + "_banner";
	let index = 0;
	let rotation;
	let paused = false;
	const select = (next) => {
		index = (next + slides.length) % slides.length;
		slides.forEach((slide, position) => {
			slide.hidden = position !== index;
		});
		presentation(popup, slides[index]);
		const counter = popup.querySelector("[data-popup-counter]");
		if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
	};
	const stop = () => clearInterval(rotation);
	const start = () => {
		stop();
		if (single || slides.length < 2 || matchMedia("(prefers-reduced-motion: reduce)").matches)
			return;
		rotation = setInterval(() => {
			const focused =
				slides[index].contains(document.activeElement) ||
				navigation?.contains(document.activeElement);
			const hovered = slides[index].matches(":hover") || navigation?.matches(":hover");
			if (!paused && !document.hidden && !hovered && !focused) select(index + 1);
		}, 6000);
	};
	if (navigation) navigation.hidden = single || slides.length < 2;
	popup
		.querySelector("[data-popup-previous]")
		?.addEventListener("click", () => select(index - 1));
	popup.querySelector("[data-popup-next]")?.addEventListener("click", () => select(index + 1));
	popup.querySelector("[data-popup-pause]")?.addEventListener("click", (event) => {
		paused = !paused;
		event.currentTarget.setAttribute("aria-pressed", String(paused));
		event.currentTarget.setAttribute(
			"aria-label",
			paused ? "Resume banner rotation" : "Pause banner rotation"
		);
		event.currentTarget.textContent = paused ? "▶" : "Ⅱ";
	});
	popup.addEventListener("close", stop);
	return (selectedIndex) => {
		if (!slides.length) return;
		if (Number.isInteger(selectedIndex)) select(selectedIndex);
		if (single) {
			try {
				const previous = localStorage.getItem(key);
				select(slides.findIndex((slide) => slide.dataset.bannerName === previous) + 1);
				localStorage.setItem(key, slides[index].dataset.bannerName);
			} catch {
				select(0);
			}
		}
		start();
		return index;
	};
}

export async function mountWebsitePopup(root = document) {
	const popup = root.querySelector("[data-website-popup]");
	if (!popup || popup.dataset.mounted || !popup.showModal) return;
	popup.dataset.mounted = "true";
	const allSlides = [...popup.querySelectorAll("[data-popup-slide]")];
	const context = await targetingContext(allSlides);
	allSlides.forEach((slide) => {
		if (!matchesRules(rulesFor(slide), context) || !frequencyAllows(slide, rulesFor(slide)))
			slide.remove();
	});
	const slides = [...popup.querySelectorAll("[data-popup-slide]")];
	if (!slides.length) return;
	mountPopupElements(popup);
	const controller = new AbortController();
	let selectedIndex = 0;
	const interval = Number(popup.dataset.repeatMinutes) * 60 * 1000;
	const delay = Math.max(0, Number(popup.dataset.delaySeconds ?? 10)) * 1000;
	const showBanners = mountBanners(popup);
	let previousFocus;
	let previousOverflow;
	let timer;
	const eligible = () => {
		const slide = slides[selectedIndex],
			rules = rulesFor(slide);
		return (
			frequencyAllows(slide, rules) &&
			(rules.frequency === "always" ||
				rules.frequency === "session" ||
				rules.frequency === "once" ||
				Date.now() - lastShown() >=
					(rules.repeat_minutes ? Number(rules.repeat_minutes) * 60000 : interval))
		);
	};
	const close = () => popup.close();
	const show = () => {
		if (!eligible() || popup.open) return;
		previousFocus = document.activeElement;
		previousOverflow = document.body.style.overflow;
		popup.showModal();
		selectedIndex = showBanners(selectedIndex) ?? selectedIndex;
		rememberSlide(slides[selectedIndex]);
		rememberShown(Date.now());
		document.body.style.overflow = "hidden";
		(
			popup.querySelector("[data-popup-close]:not([hidden])") ||
			popup.querySelector("a,button,input") ||
			popup
		).focus();
	};
	const attempt = async () => {
		if (!eligible()) return;
		if (document.hidden || document.querySelector("dialog[open], .careers-modal.is-active")) {
			timer = setTimeout(attempt, 1000);
			return;
		}
		if (navigator.locks) {
			await navigator.locks.request(STORAGE_KEY, show);
		} else {
			await claimAndShow(show);
		}
	};
	popup.querySelector("[data-popup-close]").addEventListener("click", close);
	popup.addEventListener("click", (event) => {
		const bounds = popup.getBoundingClientRect();
		if (
			event.target === popup &&
			(event.clientX < bounds.left ||
				event.clientX > bounds.right ||
				event.clientY < bounds.top ||
				event.clientY > bounds.bottom)
		)
			close();
	});
	popup.addEventListener("close", () => {
		document.body.style.overflow = previousOverflow;
		previousFocus?.focus();
	});
	window.addEventListener("storage", (event) => {
		if (event.key === STORAGE_KEY && !eligible()) clearTimeout(timer);
	});
	window.addEventListener("pagehide", () => {
		clearTimeout(timer);
		controller.abort();
		if (popup.open) close();
	});
	window.addEventListener("pageshow", (event) => {
		if (event.persisted && eligible()) timer = setTimeout(attempt, delay);
	});
	slides.forEach((slide, index) =>
		armTrigger(
			rulesFor(slide),
			() => {
				if (popup.open) return;
				selectedIndex = index;
				attempt();
			},
			Number(rulesFor(slide).default_delay_seconds ?? delay / 1000) * 1000,
			controller.signal
		)
	);
}
